import ShapeSceneAbstract from "../ShapeAnimation/ShapeSceneAbstract";
import ShapeUtils from "../ShapeAnimation/ShapeUtils";
import eventListenerOption from "../../lib/eventListenerOption";
import * as THREE from "three";
import { Vector3 } from "three";
import onDocumentLoaded from '../../lib/onDocumentLoaded';

export enum ShapeTypes {
	Torus,
	SphereBeehive,
	SphereBeachBall
}

export default class DecorativeShape extends ShapeSceneAbstract {
	private rotateStart = new THREE.Vector2();
	private rotateEnd = new THREE.Vector2();
	private rotateDelta = new THREE.Vector2();

	private rotateSpeed = 1.0;

	private isPointerActive = false;

	static init() {
		onDocumentLoaded(document, () => {
			document.querySelectorAll<HTMLElement>(".pageFooter__donutWrapper").forEach((element) => {
				const decorativeShape = new DecorativeShape(
					element,
					ShapeTypes.Torus,
					"pageFooter__donutCanvas"
				);
				decorativeShape.startRendering();
			});

			const canvasClass = "decorativeShape__canvas";
			
			document.querySelectorAll<HTMLElement>(".decorativeShape--donut").forEach((element) => {
				const decorativeShape = new DecorativeShape(
					element,
					ShapeTypes.Torus,
					canvasClass
				);
				decorativeShape.startRendering();
			});

			document.querySelectorAll<HTMLElement>(".decorativeShape--beehive").forEach((element) => {
				const decorativeShape = new DecorativeShape(
					element,
					ShapeTypes.SphereBeehive,
					canvasClass
				);
				decorativeShape.startRendering();
			});

			document.querySelectorAll<HTMLElement>(".decorativeShape--beach-ball").forEach((element) => {
				const decorativeShape = new DecorativeShape(
					element,
					ShapeTypes.SphereBeachBall,
					canvasClass
				);
				decorativeShape.startRendering();
			});

			document.querySelectorAll<HTMLElement>(".person__beachBallWrap").forEach((element) => {
				const decorativeShape = new DecorativeShape(
					element,
					ShapeTypes.SphereBeachBall,
					'person__beachBallCanvas'
				);
				decorativeShape.startRendering();
			});
		});
	}


	constructor(
		htmlContainer: HTMLElement,
		shapeType: ShapeTypes,
		canvasStyleClass: string,
		getCanvasSize?: () => {width: number, height: number}
	) {
		super(htmlContainer, canvasStyleClass, getCanvasSize);

		const maxSize = ShapeUtils.getMaxSizes(new Vector3(0,0,0), this.camera);
		let mesh: THREE.Mesh;

		if (shapeType === ShapeTypes.Torus) {

			const torusRadius = Math.min(maxSize.width, maxSize.height) / 2;
			const torusRadiusOfTube = torusRadius * 0.525;

			mesh = ShapeUtils.createTorus(
				torusRadius,
				torusRadiusOfTube,
				this.verticalStripesMaterial
			);
			mesh.scale.setScalar(torusRadius / (torusRadius + torusRadiusOfTube));

		} else if (shapeType === ShapeTypes.SphereBeehive || shapeType === ShapeTypes.SphereBeachBall) {

			const radius = Math.min(maxSize.width, maxSize.height) / 2;
			mesh = ShapeUtils.createSphere(
				radius,
				shapeType === ShapeTypes.SphereBeehive
					? this.horizontalStripesMaterial
					: this.verticalStripesMaterial
			);

		}
	
		if (mesh === undefined)  return; // This should never happen.

		mesh.quaternion.y = -Math.PI / 2 / 5;
		mesh.quaternion.x = -Math.PI / 2 / 10;

		this.shapesContainer.add(mesh);

		// Mouse/pointer events
		document.addEventListener("pointerdown", this.onPointerDown, eventListenerOption.passiveNotCapture);
		document.addEventListener("pointermove", this.onPointerMove, eventListenerOption.passiveNotCapture);
		document.addEventListener("pointerup", this.onPointerUp, eventListenerOption.passiveNotCapture);

		// Disable actions when an object is grabbed
		document.addEventListener("touchmove", this.disableTouchMove, { passive:false });
		window.addEventListener("selectstart", this.disableSelect);
	}

	private onPointerDown = (event: PointerEvent) => {
		if (
			// Main button click and not on an image which could be overlapping this webgl piece
			event.buttons === 1
			&& event.target instanceof HTMLImageElement === false
			&& event.target instanceof HTMLAnchorElement === false
		) {
			const canvasRect = this.renderer.domElement.getBoundingClientRect();
			if (
				// Test if touch is where the element is
				event.clientX >= canvasRect.x && event.clientX <= canvasRect.x + canvasRect.width
				&& event.clientY >= canvasRect.y && event.clientY <= canvasRect.y + canvasRect.height
			) {
				this.isPointerActive = true;
				this.rotateStart.set(event.clientX, event.clientY);
			}
		}
	}

	private onPointerMove = (event: PointerEvent) => {
		if (this.isPointerActive) {
			this.rotateEnd.set(event.clientX, event.clientY);
			this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(this.rotateSpeed);

			const canvasSize = this.getCanvasSize();
			const eulerDelta = new THREE.Euler(
				2 * Math.PI * this.rotateDelta.x / canvasSize.height, // yes, height
				2 * Math.PI * this.rotateDelta.y / canvasSize.height,
				0
			);

			this.shapesContainer.children.forEach(shape => {
				shape.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), eulerDelta.x);
				shape.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), eulerDelta.y);
			});
			
			this.rotateStart.set(event.clientX, event.clientY);
		}
	}

	private onPointerUp = (event: PointerEvent) => {
		if (this.isPointerActive) {
			this.isPointerActive = false;
		}
	}


	private disableTouchMove = (event: Event) => {
		if (this.isPointerActive) {
			event.preventDefault();
		}
	}
	private disableSelect = (event: Event) => {
		if (this.isPointerActive) {
			event.preventDefault();
		}
	}

	protected updateAndRender = () => {
		if (this.isRendering() === false) {
			return;
		}

		if (this.renderingTracker.isUpdateNeeded()) {
			const canvasSize = this.getCanvasSize();
			
			// Set size
			this.renderer.setSize(canvasSize.width, canvasSize.height);
			this.camera.aspect = canvasSize.width / canvasSize.height;

			// Update camera's projection matrix
			this.camera.updateProjectionMatrix();
			
			this.renderingTracker.setUpdateNeeded(false);
		}

		// Rotate shape if pointer is not active
		if (!this.isPointerActive) {
			// TODO: Keep or remove the if-statement
			this.shapesContainer.children.forEach(shape => {
				shape.rotation.x += 0.0005;
				shape.rotation.y += 0.002;
				shape.rotation.z += 0.004;
			});
		}

		this.renderer.render(this.scene, this.camera); 
	}

}